import { call, put, select, takeEvery } from 'redux-saga/effects';
import { getLocation, LOCATION_CHANGE, push } from 'connected-react-router';
import { matchPath } from 'connected-react-router';
import { addSuccess, addError } from '../notifications/actions';
import { deleteItem as deleteDialog } from '../dialog-manager/actions';
import { addItems } from '../crew-list/actions';
import { crewListModes, getCrewListBySelectedTimeplans } from '../crew-list/utils';
import { actions, constants, selector } from './redux';
import { constants as timeplansConstants } from '../timeplans/redux';
import Api from '../../utils/Api';
import { didDocumentLocationChange } from '../../utils/router';

// (Was: stopSubmit from 'redux-form'; getCrewListTableItems from
// './Form/selector'. The form is RHF-driven now. The crewList
// filter previously lived in the selector — moved into this saga
// so we can read the date out of the action payload that the form
// dispatches.)
const getCrewList = ({ crewList: { data } }) => data;

function* loadItem({ payload: { id, mode } }) {
  try {
    const { timeplansById, ...crewChange } = yield call(Api.crewChange.show, id);

    const crewListActionPayload = {
      mode,
      data: (mode === crewListModes.STORE || mode === crewListModes.DISPLAY)
        ? crewChange.crewList
        : timeplansById,
    };
    yield put(actions.addLoadedItem(crewChange));
    yield put(addItems(crewListActionPayload));
  } catch (e) {
    console.error(e);
    yield put(actions.loadItemError(e));
  }
}

// QA bug 2.4 (2026-05-19): rotation create/edit returned a BE
// 422 ("This Timeplan is already in use in another CrewList")
// that the catch path silently dropped into the redux store —
// no toast, no field-level error in the UI. User saw "nothing
// happens" → "Ротація не працює". Surface the BE-supplied error
// title (the actual reason) as a notification so the user knows
// what to fix; keep the redux-store dispatch too so any
// downstream listener still works.
const errorTitleFrom = (e) => (
  e?.apiErrors?.[0]?.title
  || e?.message
  || 'Save failed'
);

function* createItem({ payload }) {
  try {
    const fullCrewList = yield select(getCrewList);
    const crewList = getCrewListBySelectedTimeplans(fullCrewList, payload?.date);
    const crewlists = crewList.map((item) => ({
      sign_on_id: item.on_sign_id || null,
      sign_off_id: item.off_sign_id || null,
    }));
    const crewChange = yield call(Api.crewChange.create, payload, crewlists);

    yield put(push('/'));
    yield put(actions.addCreatedItem(crewChange));
    yield put(addSuccess('The crew change created successfully'));
  } catch (e) {
    console.error(e);
    yield put(addError(errorTitleFrom(e), 'Rotation save failed'));
    yield put(actions.createItemError(e));
  }
}

function* updateItem({ payload }) {
  try {
    const { pathname } = yield select(getLocation);
    const match = matchPath(pathname, { path: '/timeplans/crew-rotation/crew-change/:id/edit' });

    const crewChange = yield call(Api.crewChange.update, match.params.id, payload);

    yield put(push('/'));
    yield put(actions.updateItem(crewChange));
    yield put(addSuccess('The crew change updated successfully'));
  } catch (e) {
    console.error(e);
    yield put(addError(errorTitleFrom(e), 'Rotation save failed'));
    yield put(actions.updateItemError(e));
  }
}

function* deleteItem({ payload: id }) {
  try {
    yield call(Api.crewChange.delete, id);

    yield put(deleteDialog());
    yield put(addSuccess('Crew change deleted successfully'));
    yield put(actions.deleteItem(id));
  } catch (e) {
    console.error(e);

    yield put(actions.deleteItemError(e));
  }
}

export function* fetchItemOnPageInit(action) {
  const match = (
    didDocumentLocationChange(action, '/timeplans/crew-rotation/crew-change/:id/edit')
    || didDocumentLocationChange(action, '/crew-changes/:id/edit')
    || didDocumentLocationChange(action, '/crew-changes/:id/details')
  );

  // Guard against the saga firing with `id: undefined` — possible
  // when the CRR-shim's matchPath returns a truthy match with no
  // `:id` param. Without this the saga would call
  // `Api.crewChange.show(undefined)` and 404 against
  // `/api//crew_changes/undefined`.
  if (match && match.params && match.params.id) {
    yield put(actions.loadItemRequest({
      id: match.params.id,
      mode: crewListModes.STORE,
    }));
  }
}

function* checkDoWeNeedToReLoadCrewChange({ payload: t }) {
  try {
    const featureState = yield select(selector);
    const listFeatureState = yield select((s) => s.crewList);

    if (
      listFeatureState.mode === crewListModes.DISPLAY
      && [t.crew_change_sign_off_id, t.crew_change_sign_on_id].includes(Number(featureState.data.id))
    ) {
      yield put(actions.loadItemRequest({
        id: featureState.data.id,
        mode: crewListModes.DISPLAY,
      }));
    }
  } catch (e) {
    console.error(e);
  }
}

/**
 * getFeatureSagas (rename)
 * each saga file should return all feature sagas
 * @returns {ForkEffect[]}
 */
export default function getFeatureSagas() {
  return [
    takeEvery(constants.LOAD_ITEM_REQUEST, loadItem),
    takeEvery(constants.CREATE_ITEM_REQUEST, createItem),
    takeEvery(constants.UPDATE_ITEM_REQUEST, updateItem),
    takeEvery(constants.DELETE_ITEM_REQUEST, deleteItem),
    takeEvery(timeplansConstants.ITEM_UPDATED_SUCCESSFULLY, checkDoWeNeedToReLoadCrewChange),
    takeEvery(LOCATION_CHANGE, fetchItemOnPageInit),
  ];
}
